import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useMemoize } from '@vueuse/core'
import paymentFlowActionsApi from '@/services/http/api/paymentFlowActions'
import {
  FlowActionsPaymentResponseSchema,
  UpdateFlowActionsPaymentResponseSchema
} from '@/schemas/flowActions'
import type { FlowActionsPaymentData, PaymentId } from '@/types'

export const usePaymentFlowActionsStore = defineStore('paymentFlowActionsStore', () => {
  const loader = ref<boolean>(false)
  const paymentFlowActions = ref<FlowActionsPaymentData | null>(null)

  const canResendP24InstallmentNotification = computed<boolean>(
    () => paymentFlowActions.value?.canResendP24InstallmentNotification ?? false
  )

  const memoizedGetFlowActions = useMemoize(paymentFlowActionsApi.get)

  const fetchPaymentFlowActions = async (paymentId: PaymentId) => {
    loader.value = true
    try {
      const { data } = await memoizedGetFlowActions(paymentId)
      const validated = FlowActionsPaymentResponseSchema.parse(data)
      paymentFlowActions.value = validated.data
      return validated.data
    } finally {
      loader.value = false
    }
  }

  const updatePaymentFlowActionsFromBackendResponse = (data: unknown) => {
    const validatedData = UpdateFlowActionsPaymentResponseSchema.parse(data)
    clearDataAndCache()
    paymentFlowActions.value = validatedData.data.flow_actions
  }

  const clearData = () => {
    paymentFlowActions.value = null
  }

  const clearDataAndCache = () => {
    clearData()
    memoizedGetFlowActions.clear()
  }

  return {
    loader,
    paymentFlowActions,
    canResendP24InstallmentNotification,
    fetchPaymentFlowActions,
    updatePaymentFlowActionsFromBackendResponse,
    clearData,
    clearDataAndCache
  }
})
